import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, CheckCircle, XCircle, Clock } from 'lucide-react';

export const HRFerie = () => {
  const { user, isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    data_inizio: '',
    data_fine: '',
    giorni_totali: 0,
    motivo: ''
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/hr/ferie/requests');
      setRequests(response.data);
    } catch (error) {
      toast.error('Errore caricamento richieste');
    } finally {
      setLoading(false);
    }
  };

  // Calcolo Pasqua (algoritmo di Gauss/Anonymous Gregorian)
  const getEasterDate = (year) => {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month, day);
  };

  // Festivi italiani per un dato anno
  const getHolidays = (year) => {
    const easter = getEasterDate(year);
    const easterMonday = new Date(easter); easterMonday.setDate(easter.getDate() + 1);
    return [
      `${year}-01-01`, `${year}-01-06`,
      `${year}-${String(easterMonday.getMonth()+1).padStart(2,'0')}-${String(easterMonday.getDate()).padStart(2,'0')}`,
      `${year}-04-25`, `${year}-05-01`, `${year}-06-02`,
      `${year}-08-15`, `${year}-11-01`, `${year}-12-08`,
      `${year}-12-25`, `${year}-12-26`
    ];
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (endDate < startDate) return 0;

    const years = new Set();
    let d = new Date(startDate);
    while (d <= endDate) { years.add(d.getFullYear()); d.setDate(d.getDate() + 1); }
    const holidays = new Set();
    years.forEach(y => getHolidays(y).forEach(h => holidays.add(h)));

    let count = 0;
    d = new Date(startDate);
    while (d <= endDate) {
      const day = d.getDay(); // 0=dom, 6=sab
      const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (day !== 0 && day !== 6 && !holidays.has(dateStr)) count++;
      d.setDate(d.getDate() + 1);
    }
    return count;
  };

  const handleDateChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    if (newData.data_inizio && newData.data_fine) {
      newData.giorni_totali = calculateDays(newData.data_inizio, newData.data_fine);
    }
    setFormData(newData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/ferie/request', formData);
      toast.success('Richiesta ferie inviata!');
      setDialogOpen(false);
      setFormData({ data_inizio: '', data_fine: '', giorni_totali: 0, motivo: '' });
      fetchRequests();
    } catch (error) {
      toast.error('Errore invio richiesta');
    }
  };

  const handleApprove = async (id, approved) => {
    try {
      await api.patch(`/hr/ferie/${id}/approve?approved=${approved}`);
      toast.success(approved ? 'Ferie approvate!' : 'Ferie rifiutate');
      fetchRequests();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const statusConfig = {
    in_attesa: { label: 'In Attesa', color: 'orange', icon: Clock },
    approvata: { label: 'Approvata', color: 'green', icon: CheckCircle },
    rifiutata: { label: 'Rifiutata', color: 'red', icon: XCircle }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 md:p-8 lg:p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Gestione Ferie</h1>
          <p className="text-slate-600">Richieste e approvazioni ferie</p>
        </div>

        {!isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus size={20} className="mr-2" />
                Richiedi Ferie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuova Richiesta Ferie</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Data Inizio</Label>
                  <Input
                    type="date"
                    value={formData.data_inizio}
                    onChange={(e) => handleDateChange('data_inizio', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label>Data Fine</Label>
                  <Input
                    type="date"
                    value={formData.data_fine}
                    onChange={(e) => handleDateChange('data_fine', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label>Giorni Lavorativi</Label>
                  <Input
                    type="number"
                    value={formData.giorni_totali}
                    readOnly
                    className="bg-slate-50"
                  />
                  <p className="text-xs text-slate-400 mt-1">Esclusi sabato, domenica e festivi</p>
                </div>

                <div>
                  <Label>Motivo (opzionale)</Label>
                  <Textarea
                    value={formData.motivo}
                    onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                    rows={3}
                  />
                </div>

                <Button type="submit" className="w-full">
                  Invia Richiesta
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white rounded-xl border p-6">
        <div className="space-y-4">
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <Clock size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Nessuna richiesta ferie</p>
            </div>
          ) : (
            requests.map(request => {
              const config = statusConfig[request.stato];
              return (
                <div key={request.id} className="border rounded-lg p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900">{request.dipendente_nome}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${config.color}-50 text-${config.color}-600 border border-${config.color}-200 flex items-center gap-1`}>
                          <config.icon size={14} />
                          {config.label}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        <p><strong>Periodo:</strong> {request.data_inizio} → {request.data_fine}</p>
                        <p><strong>Giorni lavorativi:</strong> {request.giorni_totali}</p>
                        {request.motivo && <p><strong>Motivo:</strong> {request.motivo}</p>}
                        {request.approvato_da_nome && (
                          <p><strong>Gestito da:</strong> {request.approvato_da_nome}</p>
                        )}
                        {request.note_admin && (
                          <p><strong>Note:</strong> {request.note_admin}</p>
                        )}
                      </div>
                    </div>

                    {isAdmin && request.stato === 'in_attesa' && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(request.id, true)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Approva
                        </Button>
                        <Button
                          onClick={() => handleApprove(request.id, false)}
                          size="sm"
                          variant="destructive"
                        >
                          <XCircle size={16} className="mr-1" />
                          Rifiuta
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
